import { supabase } from './supabase'
import { invokeFunction, invokeFunctionAnonymous } from './supabase-functions'
import type { GmailMessage } from '../types/communication'
import { shouldSendEmail, addUnsubscribeFooter, EMAIL_CATEGORIES } from '../utils/emailPreferences'

interface EmailConfig {
  method: 'sendgrid' | 'resend'
  sendgrid?: {
    from_email: string
    from_name: string
  }
  resend?: {
    from_email: string
    from_name: string
  }
}

// Get email configuration - Resend by default
export const getEmailConfig = async (): Promise<EmailConfig> => {
  try {
    // Check if we should skip fetching (if table doesn't exist)
    if (typeof window !== 'undefined' && window.localStorage.getItem('skip_facility_settings') === 'true') {
      return {
        method: 'resend',
        resend: {
          from_email: 'noreply@app.aifacilitymanager.com',
          from_name: 'AI Facility Manager'
        }
      }
    }

    // Try to get email settings from facility_settings
    const { data: facilitySettings, error } = await supabase
      .from('facility_settings')
      .select('email_settings')
      .single()
    
    // If table doesn't exist, use defaults
    if (error) {
      // Catch all possible error codes for missing table: 42P01, PGRST116, PGRST200, 404, etc.
      const isTableMissing = error.code === '42P01' ||
                             error.code === 'PGRST116' ||
                             error.code === 'PGRST200' ||
                             error.message?.includes('404') ||
                             error.message?.includes('406') ||
                             error.message?.includes('does not exist') ||
                             error.message?.includes('Not Found')

      if (isTableMissing) {
        // Mark to skip future attempts this session
        if (typeof window !== 'undefined') {
          window.localStorage.setItem('skip_facility_settings', 'true')
        }
      }

      return {
        method: 'resend',
        resend: {
          from_email: 'noreply@app.aifacilitymanager.com',
          from_name: 'AI Facility Manager'
        }
      }
    }

    // Check for email settings
    if (facilitySettings?.email_settings) {
      const emailSettings = facilitySettings.email_settings
      
      return {
        method: 'resend',
        resend: {
          from_email: emailSettings.from_email || 'noreply@app.aifacilitymanager.com',
          from_name: emailSettings.from_name || 'AI Facility Manager'
        }
      }
    }

    // Check organization settings for custom from email/name
    const { data: { user } } = await supabase.auth.getUser()
    if (user) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('id', user.id)
        .single()

      if (profile?.organization_id) {
        const { data: organization } = await supabase
          .from('organizations')
          .select('settings')
          .eq('id', profile.organization_id)
          .single()

        if (organization?.settings?.email?.resend) {
          return {
            method: 'resend',
            resend: {
              from_email: organization.settings.email.resend.from_email || 'noreply@app.aifacilitymanager.com',
              from_name: organization.settings.email.resend.from_name || 'AI Facility Manager'
            }
          }
        }
      }
    }
  } catch (error) {
    console.log('No email settings found, using defaults')
  }

  // Default to Resend with verified domain
  return {
    method: 'resend',
    resend: {
      from_email: 'noreply@app.aifacilitymanager.com',
      from_name: 'AI Facility Manager'
    }
  }
}

// Send email via SendGrid edge function - PRODUCTION READY
const sendViaSendGrid = async (message: GmailMessage, config: EmailConfig['sendgrid'], anonymous: boolean = false): Promise<string> => {
  if (!config) throw new Error('SendGrid configuration not provided')

  console.log('📧 Sending email via SendGrid edge function...')
  console.log('Email config:', config)
  console.log('Sending to:', message.to)
  console.log('Subject:', message.subject)
  console.log('Anonymous mode:', anonymous)

  const payload = {
    to: message.to,
    from: `${config.from_name} <${config.from_email}>`,
    subject: message.subject,
    html: message.body,
    replyTo: config.from_email,
    attachments: message.attachments
  }

  console.log('Edge function payload:', JSON.stringify(payload, null, 2))

  try {
    console.log('Invoking edge function...')
    const { data, error } = anonymous
      ? await invokeFunctionAnonymous('send-email', payload)
      : await invokeFunction('send-email', payload)

    if (error) {
      console.error('❌ Edge Function Error:', error)
      throw new Error(`Edge Function failed: ${error.message}`)
    }

    // Check if we got a response
    if (!data) {
      console.error('❌ No response from Edge Function')
      throw new Error('No response from email service')
    }

    // Log the full response for debugging
    console.log('Edge Function Response:', data)

    // Check for various error conditions
    if (data?.error?.includes('SendGrid API key not configured') || 
        data?.message?.includes('SendGrid API key not configured')) {
      console.warn('⚠️ SendGrid API key not configured')
      throw new Error('Email service not configured. Please add SendGrid API key to Edge Function secrets.')
    }
    
    if (data?.message?.includes('simulated')) {
      console.warn('⚠️ Email was simulated, not actually sent')
      throw new Error('Email was simulated - SendGrid not properly configured.')
    }

    if (!data.success) {
      console.error('❌ Email Send Failed:', data.error || data.message)
      throw new Error(data.error || data.message || 'Failed to send email')
    }

    console.log('✅ Email sent successfully!')
    return data.messageId || `email_${Date.now()}`
  } catch (error: unknown) {
    console.error('❌ SendGrid send error:', error)
    // Re-throw with more context
    throw new Error(`Email send failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

// Send email via Resend edge function
const sendViaResend = async (message: GmailMessage, config: EmailConfig['resend'], anonymous: boolean = false): Promise<string> => {
  if (!config) throw new Error('Resend configuration not provided')

  console.log('📧 Sending email via Resend edge function...')
  console.log('Email config:', config)
  console.log('Sending to:', message.to)
  console.log('Subject:', message.subject)
  console.log('Anonymous mode:', anonymous)

  const payload = {
    to: message.to,
    from: `${config.from_name} <${config.from_email}>`,
    subject: message.subject,
    html: message.body,
    replyTo: config.from_email,
    attachments: message.attachments
  }

  console.log('Edge function payload:', JSON.stringify(payload, null, 2))

  try {
    console.log('Invoking edge function...')
    const { data, error } = anonymous
      ? await invokeFunctionAnonymous('send-email-resend', payload)
      : await invokeFunction('send-email-resend', payload)

    if (error) {
      console.error('❌ Edge Function Error:', error)
      throw new Error(`Edge Function failed: ${error.message}`)
    }

    // Check if we got a response
    if (!data) {
      console.error('❌ No response from Edge Function')
      throw new Error('No response from email service')
    }

    // Log the full response for debugging
    console.log('Edge Function Response:', data)

    // Check for various error conditions
    if (data?.error?.includes('Resend API key not configured') || 
        data?.message?.includes('Resend API key not configured')) {
      console.warn('⚠️ Resend API key not configured')
      throw new Error('Email service not configured. Please add Resend API key to Edge Function secrets.')
    }

    if (!data.success) {
      console.error('❌ Email Send Failed:', data.error || data.message)
      throw new Error(data.error || data.message || 'Failed to send email')
    }

    console.log('✅ Email sent successfully!')
    return data.messageId || `email_${Date.now()}`
  } catch (error: unknown) {
    console.error('❌ Resend send error:', error)
    // Re-throw with more context
    throw new Error(`Email send failed: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

// Main send email function - Supports both SendGrid and Resend
export const sendEmail = async (
  message: GmailMessage,
  options?: {
    clientId?: string
    emailType?: keyof typeof EMAIL_CATEGORIES
    skipPreferenceCheck?: boolean
    anonymous?: boolean
  }
): Promise<string> => {
  // Check email preferences if clientId and emailType provided
  if (options?.clientId && options?.emailType && !options?.skipPreferenceCheck) {
    const { send, reason } = await shouldSendEmail(options.clientId, options.emailType)
    if (!send) {
      console.log(`📧 Email not sent: ${reason}`)
      return 'skipped_due_to_preferences'
    }
  }

  // Add unsubscribe footer for marketing emails
  let finalBody = message.body
  if (options?.clientId && options?.emailType) {
    const category = EMAIL_CATEGORIES[options.emailType]
    if (category && ['marketing', 'newsletter', 'promotional'].includes(category.type)) {
      finalBody = await addUnsubscribeFooter(message.body, options.clientId, true)
    }
  }

  const config = await getEmailConfig()

  console.log(`📧 Sending email via ${config.method}`)
  console.log(`📧 Anonymous mode: ${options?.anonymous || false}`)

  // Update message with potentially modified body
  const messageWithFooter = { ...message, body: finalBody }

  // Use Resend by default, fallback to SendGrid
  if (config.method === 'resend' && config.resend) {
    return sendViaResend(messageWithFooter, config.resend, options?.anonymous || false)
  } else if (config.sendgrid) {
    return sendViaSendGrid(messageWithFooter, config.sendgrid, options?.anonymous || false)
  } else {
    throw new Error('No email service configured')
  }
}

// Send welcome email with password setup link
export const sendWelcomeEmail = async (data: {
  to: string
  fullName: string
  organizationName?: string
  temporaryPassword?: string
}): Promise<{ success: boolean; error?: string }> => {
  console.log('sendWelcomeEmail called with:', { 
    to: data.to, 
    fullName: data.fullName,
    hasPassword: !!data.temporaryPassword 
  })
  
  try {
    const config = await getEmailConfig()
    const companyName = data.organizationName || config.resend?.from_name || config.sendgrid?.from_name || 'AI Facility Manager'
    
    // Get organization theme colors if available
    let primaryColor = '#22d3ee' // Default cyan color (brand)
    let backgroundColor = '#f8f9fa'
    let textColor = '#1f2937'
    
    // Try to get organization settings for theme colors
    try {
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('organization_id')
          .eq('id', user.id)
          .single()
        
        if (profile?.organization_id) {
          const { data: org } = await supabase
            .from('organizations')
            .select('settings')
            .eq('id', profile.organization_id)
            .single()
          
          if (org?.settings?.primaryColor) {
            primaryColor = org.settings.primaryColor
          }
          if (org?.settings?.backgroundColor) {
            backgroundColor = org.settings.backgroundColor
          }
          if (org?.settings?.textColor) {
            textColor = org.settings.textColor
          }
        }
      }
    } catch (e) {
      // Use defaults if we can't get org settings
      console.log('Using default colors for email')
    }
    
    // For new users, we'll include instructions on how to set a password
    // Note: We can't generate a password reset link from the frontend for another user
    const passwordResetUrl = `${window.location.origin}/login`
    
    // Determine the appropriate message based on the context
    let passwordSection: string

    if (data.temporaryPassword) {
      // User was given a temporary password (admin created account)
      passwordSection = `
      <div style="margin: 20px 0; padding: 20px; background-color: ${backgroundColor}; border-radius: 6px; border: 1px solid #e5e7eb;">
        <p style="margin: 0 0 10px 0; color: ${textColor}; font-size: 14px; font-weight: 600;">
          Your login credentials:
        </p>
        <p style="margin: 0 0 5px 0; color: ${textColor}; font-size: 16px;">
          <strong>Email:</strong> ${data.to}
        </p>
        <p style="margin: 0; color: ${textColor}; font-size: 16px;">
          <strong>Temporary Password:</strong> <span style="font-family: monospace; background: #e8e8e8; padding: 4px 8px; border-radius: 4px; font-size: 14px;">${data.temporaryPassword}</span>
        </p>
      </div>

      <div style="margin: 30px 0;">
        <h3 style="margin: 0 0 15px 0; color: ${textColor}; font-size: 18px; font-weight: 600;">
          Getting Started:
        </h3>
        <p style="margin: 0 0 20px 0; color: #6b7280; font-size: 16px; line-height: 1.5;">
          You can sign in immediately with the temporary password above.
        </p>

        <!-- Action buttons -->
        <div style="text-align: center; margin: 30px 0;">
          <a href="${passwordResetUrl}"
             style="display: inline-block; padding: 12px 32px; background: ${primaryColor}; color: #ffffff; text-decoration: none; border-radius: 6px; font-weight: 600; font-size: 16px; margin: 0 10px 10px 0;">
            Sign In Now
          </a>
          <a href="${window.location.origin}/login#forgot-password"
             style="display: inline-block; padding: 12px 32px; background: #ffffff; color: ${primaryColor}; text-decoration: none; border-radius: 6px; font-weight: 600; font-size: 16px; border: 2px solid ${primaryColor};">
            Reset Password
          </a>
        </div>

        <p style="margin: 20px 0 0 0; color: #6b7280; font-size: 14px; text-align: center;">
          <strong>Security tip:</strong> We recommend changing your temporary password after your first login.<br>
          You can also use the "Forgot Password" link on the login page to set a new password.
        </p>
      </div>
    `
    } else if (data.organizationName) {
      // User was invited to an existing organization (has their own account)
      passwordSection = `
      <div style="margin: 20px 0; padding: 20px; background-color: ${backgroundColor}; border-radius: 6px; border: 1px solid #e5e7eb;">
        <p style="margin: 0 0 10px 0; color: ${textColor}; font-size: 16px; line-height: 1.5;">
          Welcome! You've been added to <strong>${data.organizationName}</strong>.
        </p>
        <p style="margin: 0; color: #6b7280; font-size: 14px;">
          You already have an account. Please use your existing password to sign in.
        </p>
      </div>

      <div style="text-align: center; margin: 30px 0;">
        <a href="${passwordResetUrl}"
           style="display: inline-block; padding: 12px 32px; background: ${primaryColor}; color: #ffffff; text-decoration: none; border-radius: 6px; font-weight: 600; font-size: 16px; margin-right: 10px;">
          Sign In
        </a>
        <a href="${window.location.origin}/login#forgot-password"
           style="display: inline-block; padding: 12px 32px; background: #ffffff; color: ${primaryColor}; text-decoration: none; border-radius: 6px; font-weight: 600; font-size: 16px; border: 2px solid ${primaryColor};">
          Forgot Password?
        </a>
      </div>
    `
    } else {
      // New user signup - needs to confirm email first
      passwordSection = `
      <div style="margin: 20px 0; padding: 25px; background: linear-gradient(135deg, #667eea 0%, #764ba2 100%); border-radius: 8px; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">
        <p style="margin: 0 0 15px 0; color: #ffffff; font-size: 18px; font-weight: 600; text-align: center;">
          📧 Please Confirm Your Email
        </p>
        <p style="margin: 0 0 10px 0; color: #ffffff; font-size: 15px; line-height: 1.6; text-align: center;">
          We've sent you a confirmation email with a link to verify your account.
        </p>
        <p style="margin: 0; color: rgba(255,255,255,0.9); font-size: 14px; text-align: center;">
          <strong>You must click the confirmation link before you can sign in.</strong>
        </p>
      </div>

      <div style="margin: 30px 0;">
        <h3 style="margin: 0 0 15px 0; color: ${textColor}; font-size: 18px; font-weight: 600;">
          What's Next:
        </h3>
        <ol style="margin: 0; padding-left: 20px; color: #6b7280; font-size: 15px; line-height: 1.8;">
          <li style="margin-bottom: 10px;">Check your email inbox for a message from us</li>
          <li style="margin-bottom: 10px;">Click the <strong>"Confirm your email"</strong> link in that email</li>
          <li style="margin-bottom: 10px;">Return to the login page and sign in with your credentials</li>
        </ol>
      </div>

      <div style="text-align: center; margin: 30px 0; padding: 20px; background-color: #f8f9fa; border-radius: 6px;">
        <p style="margin: 0 0 15px 0; color: #6b7280; font-size: 14px;">
          <strong>Didn't receive the confirmation email?</strong>
        </p>
        <p style="margin: 0; color: #9ca3af; font-size: 13px;">
          Check your spam folder, or contact support if you need assistance.
        </p>
      </div>
    `
    }

    const emailHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
        </head>
        <body style="margin: 0; padding: 0; background-color: #f4f4f4; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
          <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #f4f4f4; padding: 20px 0;">
            <tr>
              <td align="center">
                <table cellpadding="0" cellspacing="0" border="0" width="600" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                  <!-- Header -->
                  <tr>
                    <td style="background-color: ${primaryColor}; padding: 40px 30px; text-align: center;">
                      <h1 style="margin: 0; color: #ffffff; font-size: 32px; font-weight: 700; letter-spacing: -0.5px;">${companyName}</h1>
                      <p style="margin: 10px 0 0 0; color: rgba(255, 255, 255, 0.8); font-size: 14px; letter-spacing: 0.5px; text-transform: uppercase;">Professional Fitness Facility</p>
                    </td>
                  </tr>
                  
                  <!-- Main Content -->
                  <tr>
                    <td style="padding: 40px 30px;">
                      <h2 style="margin: 0 0 20px 0; color: #333; font-size: 24px; font-weight: 600;">Welcome, ${data.fullName}!</h2>
                      <p style="margin: 0 0 15px 0; color: #666; font-size: 16px; line-height: 1.5;">
                        Your account has been created successfully. ${data.organizationName ? `You can now access ${data.organizationName}.` : ''}
                      </p>
                      
                      ${passwordSection}
                      
                      <div style="margin-top: 30px; padding: 20px; background-color: #f8f8f8; border-radius: 6px;">
                        <p style="margin: 0 0 10px 0; color: #666; font-size: 14px; font-weight: 600;">
                          Need help?
                        </p>
                        <p style="margin: 0; color: #666; font-size: 14px; line-height: 1.5;">
                          If you have any issues signing in or need assistance, please contact your administrator.
                        </p>
                      </div>
                    </td>
                  </tr>
                  
                  <!-- Footer -->
                  <tr>
                    <td style="background-color: #f8f8f8; padding: 30px; border-top: 1px solid #e0e0e0; text-align: center;">
                      <p style="margin: 0 0 10px 0; color: #666; font-size: 14px;"><strong>${companyName}</strong></p>
                      <p style="margin: 0 0 20px 0; color: #888; font-size: 13px;">Professional Fitness Facility</p>
                      <p style="margin: 0; color: #999; font-size: 12px;">
                        This is an automated message. Please do not reply to this email.
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </body>
      </html>
    `
    
    console.log('Sending email via sendEmail function...')
    const messageId = await sendEmail({
      to: data.to,
      subject: `Welcome to ${companyName}`,
      body: emailHtml
    }, {
      anonymous: true  // Use anonymous mode during signup (no session yet)
    })

    console.log('Email sent successfully, messageId:', messageId)
    return { success: true }
  } catch (error: unknown) {
    console.error('Failed to send welcome email:', error)
    console.error('Error details:', error instanceof Error ? error.stack : 'No stack trace')
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to send welcome email'
    }
  }
}

// Send password reset email
export const sendPasswordResetEmail = async (data: {
  to: string
  resetLink: string
  organizationName?: string
  isNewAccount?: boolean
}): Promise<{ success: boolean; error?: string }> => {
  try {
    const config = await getEmailConfig()
    const companyName = data.organizationName || config.resend?.from_name || 'AI Facility Manager'
    
    // Different text for new accounts vs password resets
    const headerText = data.isNewAccount ? 'Welcome! Set Your Password' : 'Password Reset Request'
    const introText = data.isNewAccount
      ? `Welcome to ${companyName}! An account has been created for you.`
      : `We received a request to reset your password for your ${companyName} account.`
    const actionText = data.isNewAccount
      ? 'To get started, please set your password by clicking the button below:'
      : 'Click the button below to create a new password:'
    const buttonText = data.isNewAccount ? 'Set Your Password' : 'Reset Your Password'
    const warningTitle = data.isNewAccount ? 'Security Notice' : "Didn't request this?"
    const warningText = data.isNewAccount
      ? 'This link will expire in 1 hour for security reasons. If you did not expect this email, please contact your administrator.'
      : "If you didn't request a password reset, please ignore this email. Your password won't be changed unless you click the link above."
    
    const emailHtml = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
        </head>
        <body style="margin: 0; padding: 0; background-color: #f4f4f4; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;">
          <table cellpadding="0" cellspacing="0" border="0" width="100%" style="background-color: #f4f4f4; padding: 20px 0;">
            <tr>
              <td align="center">
                <table cellpadding="0" cellspacing="0" border="0" width="600" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.1);">
                  <!-- Header -->
                  <tr>
                    <td style="background-color: #1f2937; padding: 40px 30px; text-align: center;">
                      <h1 style="margin: 0; color: #ffffff; font-size: 28px; font-weight: 600;">${headerText}</h1>
                    </td>
                  </tr>
                  
                  <!-- Main Content -->
                  <tr>
                    <td style="padding: 40px 30px;">
                      <p style="margin: 0 0 20px 0; color: #374151; font-size: 16px; line-height: 1.5;">
                        ${introText}
                      </p>
                      
                      <p style="margin: 0 0 30px 0; color: #374151; font-size: 16px; line-height: 1.5;">
                        ${actionText}
                      </p>
                      
                      <div style="text-align: center; margin: 30px 0;">
                        <a href="${data.resetLink}" 
                           style="display: inline-block; padding: 14px 40px; background: #3b82f6; color: #ffffff; text-decoration: none; border-radius: 6px; font-weight: 600; font-size: 16px;">
                          ${buttonText}
                        </a>
                      </div>
                      
                      <p style="margin: 30px 0 20px 0; color: #6b7280; font-size: 14px; line-height: 1.5;">
                        Or copy and paste this link into your browser:
                      </p>
                      
                      <p style="margin: 0 0 30px 0; padding: 15px; background-color: #f3f4f6; border-radius: 6px; font-family: monospace; font-size: 13px; color: #374151; word-break: break-all;">
                        ${data.resetLink}
                      </p>
                      
                      <div style="margin-top: 30px; padding: 20px; background-color: #fef3c7; border-radius: 6px; border: 1px solid #fbbf24;">
                        <p style="margin: 0 0 10px 0; color: #92400e; font-size: 14px; font-weight: 600;">
                          ${warningTitle}
                        </p>
                        <p style="margin: 0; color: #92400e; font-size: 14px; line-height: 1.5;">
                          ${warningText}
                        </p>
                      </div>
                      
                      <p style="margin: 30px 0 0 0; color: #6b7280; font-size: 13px; line-height: 1.5;">
                        This password reset link will expire in 1 hour for security reasons.
                      </p>
                    </td>
                  </tr>
                  
                  <!-- Footer -->
                  <tr>
                    <td style="background-color: #f9fafb; padding: 30px; border-top: 1px solid #e5e7eb; text-align: center;">
                      <p style="margin: 0; color: #6b7280; font-size: 13px;">
                        &copy; ${new Date().getFullYear()} ${companyName}. All rights reserved.
                      </p>
                    </td>
                  </tr>
                </table>
              </td>
            </tr>
          </table>
        </body>
      </html>
    `
    
    const subject = data.isNewAccount 
      ? `Welcome to ${companyName} - Set your password`
      : `Reset your ${companyName} password`
    
    const messageId = await sendEmail({
      to: data.to,
      subject: subject,
      body: emailHtml
    })
    
    return { success: true }
  } catch (error: unknown) {
    console.error('Failed to send password reset email:', error)
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to send password reset email'
    }
  }
}

// Export for backward compatibility
export { sendViaSendGrid }